import { ApiResponse } from '@interfaces/index';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiUrlConfig } from './configs/apiUrlConfig';
import createApiClient from './configs/httpHandler';

export const userActionsApiHandler = {
  createUserAction: async (payload: CreateUserActionPayload): Promise<ApiResponse<UserAction>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).post(`/create-user-action`, payload),

  addActionsToUserAction: async (
    payload: AddActionsToUserActionPayload,
  ): Promise<ApiResponse<UserAction[]>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).post(`/add-actions-to-user-action`, payload),

  updateUserAction: async (
    actionId: number,
    payload: UpdateUserActionPayload,
  ): Promise<ApiResponse<UserAction>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).patch(
      `/update-user-action/${actionId}`,
      payload,
    ),

  removeUserAction: async (actionId: number): Promise<ApiResponse<null>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).delete(`/remove-user-action/${actionId}`),

  /**
   * TRAUMA_CONTACT action — BE sends an auto-generated email to the address on the
   * action library row (`url`). Recipient is resolved server-side from `actionId`.
   */
  sendActionContactEmail: async (
    payload: SendActionContactEmailPayload,
  ): Promise<ApiResponse<SendActionContactEmailData>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).post(`/send-action-contact-email`, payload),

  getUserActions: async (params?: GetUserActionsParams): Promise<ApiResponse<UserAction[]>> => {
    const queryParams = new URLSearchParams();
    if (params?.completed !== undefined) {
      queryParams.append('completed', params.completed.toString());
    }

    if (params?.started !== undefined) {
      queryParams.append('started', params.started.toString());
    }

    if (params?.actionType) {
      queryParams.append('actionType', params.actionType);
    }

    /** When set (e.g. advisor viewing a participant), backend returns that user's actions. */
    if (params?.userId !== undefined) {
      queryParams.append('userId', params.userId.toString());
    }

    if (params?.traumaOnly !== undefined) {
      queryParams.append('traumaOnly', params.traumaOnly.toString());
    }

    const queryString = queryParams.toString();
    const url = queryString ? `/get-user-actions?${queryString}` : '/get-user-actions';

    return createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get(url);
  },

  getInsightsUserActions: async (insightId: number): Promise<ApiResponse<UserAction[]>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get(
      `/get-recommended-indicator-actions/${insightId}`,
    ),

  getQuestionnaireUserActions: async (
    questionnaireId: number,
  ): Promise<ApiResponse<UserAction[]>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get(
      `/get-recommended-questionnaire-actions/${questionnaireId}`,
    ),

  // REFERENCE CAN BE QUESTIONNAIRE ID OR LEARNING ID
  getUserActionsByReference: async (referenceId: number): Promise<ApiResponse<UserAction[]>> =>
    createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).get(
      `/get-user-actions-by-reference/${referenceId}`,
    ),
};

/**
 * POST /v2/get-participant-actions
 * Fetch actions for a specific participant (advisor / superAdmin context).
 * Body: { userId: number, completed?: "true" | "false" }
 */
export const getParticipantActions = async (
  userId: number,
  completed?: 'true' | 'false',
): Promise<ApiResponse<UserAction[]>> =>
  createApiClient(apiUrlConfig.EXPRESS_BACKEND_V2).post(`/get-participant-actions`, {
    userId,
    ...(completed !== undefined ? { completed } : {}),
  });

/**
 * Fetch actions for a participant (advisor context). Backend must support ?userId= when caller is advisor.
 */
export const getParticipantUserActions = async (
  participantId: number,
  params?: Omit<GetUserActionsParams, 'userId'>,
): Promise<ApiResponse<UserAction[]>> =>
  userActionsApiHandler.getUserActions({ ...params, userId: participantId });

export type ActionType = 'USER_ACTION' | 'LEARNING' | 'DEEP_DIVE' | 'EXPERTS' | 'TRAUMA_CONTACT';

export interface UserAction {
  id: number;
  actionType: ActionType;
  userId: number;
  actionLibraryId: number | null;
  title: string | null;
  description: string | null;
  actionButtonTitle: string | null;
  url: string | null;
  dueDate: string;
  orderId: number;
  started: boolean;
  startedAt: string | null;
  completed: boolean;
  completedAt: string | null;
  removed: boolean;
  removedAt: string | null;
  createdBy: string;
  createdByUserId: number;
  createdAt: string;
  updatedAt: string;
  dueDays: number;
  actionImage: string | null;
}

export interface CreateUserActionPayload {
  userId: number;
  title: string;
  description: string | null;
  actionButtonTitle: string | null;
  url: string | null;
  dueDate: string | null;
  createdBy: string;
}

export interface AddActionsToUserActionPayload {
  questionnaireId: number;
  focusAreas: number[];
  indicators: number[] | null;
}

export interface UpdateUserActionPayload {
  dueDate: string | null;
  started: boolean | null;
  completed: boolean | null;
}

export interface SendActionContactEmailPayload {
  /** Action whose library `url` holds the recipient email (resolved server-side). */
  actionId: number;
}

export interface SendActionContactEmailData {
  sent: boolean;
}

export interface GetUserActionsParams {
  completed?: boolean;
  started?: boolean;
  actionType?: ActionType;
  /** For advisor context: fetch actions for this user instead of the logged-in user. */
  userId?: number;
  /** When true, returns only trauma-related user actions. */
  traumaOnly?: boolean;
}

export const useUserActionsHooks = {
  // GET /get-user-actions
  useUserActions: (params?: GetUserActionsParams) =>
    useQuery({
      queryKey: ['user-actions', params],
      queryFn: () => userActionsApiHandler.getUserActions(params),
      // staleTime: 2 * 60 * 1000, // 2 minutes
    }),

  // POST /create-user-action
  useCreateUserAction: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: (payload: CreateUserActionPayload) =>
        userActionsApiHandler.createUserAction(payload),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['user-actions'] });
      },
      onError: (error) => {
        console.error('Failed to create user action:', error);
      },
    });
  },

  // POST /add-actions-to-user-action
  useAddActionsToUserAction: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: (payload: AddActionsToUserActionPayload) =>
        userActionsApiHandler.addActionsToUserAction(payload),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['user-actions'] });
      },
      onError: (error) => {
        console.error('Failed to add actions to user action:', error);
      },
    });
  },

  // PATCH /update-user-action/{actionId}
  useUpdateUserAction: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: ({ actionId, payload }: { actionId: number; payload: UpdateUserActionPayload }) =>
        userActionsApiHandler.updateUserAction(actionId, payload),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['user-actions'] });
      },
      onError: (error) => {
        console.error('Failed to update user action:', error);
      },
    });
  },

  // DELETE /remove-user-action/{actionId}
  useRemoveUserAction: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: (actionId: number) => userActionsApiHandler.removeUserAction(actionId),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['user-actions'] });
      },
      onError: (error) => {
        console.error('Failed to remove user action:', error);
      },
    });
  },

  // POST /send-action-contact-email
  useSendActionContactEmail: () =>
    useMutation({
      mutationFn: (payload: SendActionContactEmailPayload) =>
        userActionsApiHandler.sendActionContactEmail(payload),
    }),

  useGetQuestionnaireActions: (questionnaireId: number | null) =>
    useQuery({
      queryKey: ['questionnaire-actions', questionnaireId],
      queryFn: () => userActionsApiHandler.getQuestionnaireUserActions(questionnaireId || 0),
      enabled: !!questionnaireId && questionnaireId !== 0,
    }),

  useGetInsightActions: (insightId: number | null) =>
    useQuery({
      queryKey: ['insight-actions', insightId],
      queryFn: () => userActionsApiHandler.getInsightsUserActions(insightId || 0),
      enabled: !!insightId && insightId !== 0,
    }),

  useGetUserActionsByReference: (referenceId: number | null) =>
    useQuery({
      queryKey: ['user-actions-by-reference', referenceId],
      queryFn: () => userActionsApiHandler.getUserActionsByReference(referenceId || 0),
      enabled: !!referenceId && referenceId !== 0,
    }),
};
